'use client';

import React from 'react';
import { useAuth } from 'src/context/auth';
import { isSuperUser } from 'src/components/PermissionGuard/checks';
import type {
  EvidenceReasoning,
  TriggerEvidence,
  TriggerSignal,
} from 'src/api/redux/profileConfiguration/types';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import {
  formatSignalType,
} from 'src/components/Profiles/shared/utils/signalGrouping';
import SectionCard from 'src/components/Profiles/shared/SectionCard';
import SignalGroupHeader from './SignalGroupHeader';

interface SignalGroup {
  type: string;
  label: string;
  items: SignalWithEvidence[];
  matchedCount: number;
  totalCount: number;
}

interface SignalWithEvidence {
  signal: TriggerSignal;
  evidence?: TriggerEvidence;
}

const formatOperator = (operator: string): string =>
  operator.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Match a signal definition to its corresponding evidence entry.
 * Different signal types use different keys to correlate.
 */
const findMatchingEvidence = (
  signal: TriggerSignal,
  evidenceList: TriggerEvidence[]
): TriggerEvidence | undefined => {
  const byType = evidenceList.filter((e) => e.signal_type === signal.type);
  if (byType.length <= 1) return byType[0];

  // Multiple evidence entries of the same type — match by identifying field
  if (signal.strategy_id != null) {
    return byType.find((e) => e.strategy_id === signal.strategy_id);
  }
  if (signal.code_set_id != null) {
    return byType.find((e) => e.code_set_id === signal.code_set_id);
  }
  if (signal.company_id != null) {
    return byType.find((e) => e.company_id === signal.company_id);
  }
  return byType[0];
};

const buildGroups = (
  signals: TriggerSignal[],
  evidence: TriggerEvidence[]
): SignalGroup[] => {
  const groupMap = new Map<string, SignalGroup>();

  for (const signal of signals) {
    if (!signal.type) continue;
    const ev = findMatchingEvidence(signal, evidence);
    const item: SignalWithEvidence = { signal, evidence: ev };
    const existing = groupMap.get(signal.type);

    if (existing) {
      existing.items.push(item);
      existing.totalCount++;
      if (ev?.matched) existing.matchedCount++;
    } else {
      groupMap.set(signal.type, {
        type: signal.type,
        label: formatSignalType(signal.type),
        items: [item],
        matchedCount: ev?.matched ? 1 : 0,
        totalCount: 1,
      });
    }
  }

  return Array.from(groupMap.values());
};

const formatActualValue = (ev: TriggerEvidence): string | null => {
  if (ev.actual != null) return ev.actual; // specialty
  if (ev.actual_value == null) return null;
  if (ev.signal_type === 'open_payments') return formatCurrency(ev.actual_value);
  if (ev.metric === 'percentage') return `${Math.round(ev.actual_value)}%`;
  return `${ev.actual_value.toLocaleString()} ${ev.metric ?? ''}`.trim();
};

interface PlayEvidenceListProps {
  evidence: TriggerEvidence[];
  signals?: TriggerSignal[];
  triggerLogic: 'all' | 'any';
  playEvidence?: EvidenceReasoning;
}

const PlayEvidenceList: React.FC<PlayEvidenceListProps> = ({
  evidence,
  signals,
  triggerLogic,
  playEvidence,
}) => {
  const { user } = useAuth();

  if (!isSuperUser(user)) return null;

  const validEvidence = evidence.filter((e) => e.signal_type);
  const validSignals = signals?.filter((s) => s.type);
  const hasEvidence = validEvidence.length > 0;
  const hasSignals = validSignals && validSignals.length > 0;

  if (!hasSignals && !playEvidence) return null;

  const groups = hasSignals ? buildGroups(validSignals!, validEvidence) : [];
  const matchedCount = hasEvidence
    ? validEvidence.filter((e) => e.matched).length
    : 0;

  return (
    <SectionCard
      label="Signals & Evidence"
      defaultOpen={false}
      data-testid="play-evidence"
    >
      {hasSignals && (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3">
            <h3
              className="text-base font-bold text-primary"
              data-testid="signals-heading"
            >
              Signals
            </h3>
            <p className="text-sm font-semibold text-secondary">
              {hasEvidence
                ? `${matchedCount}/${validEvidence.length} matched${triggerLogic === 'any' ? ', any required' : ', all required'}`
                : `${validSignals.length} criteria${triggerLogic === 'any' ? ', any required' : ', all required'}`}
            </p>
          </div>
          <div className="flex flex-col">
            {groups.map((group, groupIdx) => (
              <div
                key={`${group.type}-${groupIdx}`}
                data-testid={`signal-group-${group.type}`}
              >
                <SignalGroupHeader
                  label={group.label}
                  matchedCount={hasEvidence ? group.matchedCount : undefined}
                  totalCount={group.totalCount}
                />
                {group.items.map(({ signal, evidence: ev }, idx) => {
                  const isFreshFaces = signal.type === 'fresh_faces';
                  const matched = ev?.matched;

                  let primaryLabel: string;
                  let subtitle: string | undefined;

                  if (isFreshFaces) {
                    primaryLabel = signal.strategy_label ?? signal.value;
                    subtitle = signal.strategy_description;
                  } else if (signal.comparator) {
                    const isOpenPayments = signal.type === 'open_payments';
                    const unit = isOpenPayments ? '' : signal.metric === 'percentage' ? '%' : ` ${signal.metric ?? ''}`;
                    const formattedVal = isOpenPayments ? formatCurrency(Number(signal.value)) : `${Number(signal.value).toLocaleString()}${unit}`;
                    const threshold = `${formatOperator(signal.comparator)} ${formattedVal}`;
                    const identifyingLabel = signal.code_set?.label ?? signal.company_name;
                    primaryLabel = identifyingLabel ?? threshold;
                    subtitle = identifyingLabel ? threshold : undefined;
                  } else {
                    primaryLabel = signal.company_name ?? signal.value;
                  }

                  const actualValue = ev ? formatActualValue(ev) : null;

                  const dateRange =
                    ev?.min_date || ev?.max_date
                      ? ev.min_date && ev.max_date
                        ? `${ev.min_date} – ${ev.max_date}`
                        : ev.min_date ?? ev.max_date
                      : null;

                  return (
                    <div
                      key={idx}
                      className={`flex items-start justify-between gap-4 px-4 py-3 ${
                        !(
                          idx === group.items.length - 1 &&
                          groupIdx === groups.length - 1
                        )
                          ? 'border-b border-neutral-100'
                          : ''
                      }`}
                      data-testid={`signal-row-${signal.type}-${idx}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${
                            hasEvidence
                              ? matched
                                ? 'bg-green-500'
                                : 'bg-red-400'
                              : 'bg-neutral-300'
                          }`}
                          aria-label={
                            hasEvidence
                              ? matched
                                ? 'matched'
                                : 'not matched'
                              : 'not evaluated'
                          }
                        />
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <div
                            className="font-medium text-sm text-neutral-900"
                            data-testid={`signal-label-${signal.type}-${idx}`}
                          >
                            {primaryLabel}
                          </div>
                          {subtitle && (
                            <div className="text-xs text-neutral-400">
                              {subtitle}
                            </div>
                          )}
                          {dateRange && (
                            <div className="text-xs text-neutral-400">
                              {dateRange}
                            </div>
                          )}
                        </div>
                      </div>
                      {hasEvidence && actualValue != null && (
                        <div
                          className={`text-sm font-semibold whitespace-nowrap shrink-0 ${
                            matched ? 'text-neutral-900' : 'text-red-500'
                          }`}
                          data-testid={`signal-value-${signal.type}-${idx}`}
                        >
                          {actualValue}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
      {playEvidence && (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <div className="flex flex-col gap-3 px-5 py-4" data-testid="play-evidence-section">
            <div className="flex flex-col gap-1">
              <h3
                className="text-base font-bold text-primary"
                data-testid="evidence-heading"
              >
                Evidence
              </h3>
              <p className="text-sm text-secondary">
                {playEvidence.confidence_rationale}
              </p>
            </div>
            {playEvidence.references.length > 0 && (
              <div
                className="flex flex-col"
                data-testid="evidence-references"
              >
                {playEvidence.references.map((ref, idx) => (
                  <div
                    key={idx}
                    className={`flex flex-col gap-0.5 border-l-2 border-neutral-200 px-4 py-3${
                      idx < playEvidence.references.length - 1
                        ? ' border-b border-b-neutral-100'
                        : ''
                    }`}
                    data-testid={`evidence-reference-${ref.type}-${idx}`}
                  >
                    {ref.source_text && (
                      <p className="text-xs text-neutral-600 italic leading-snug">
                        &ldquo;{ref.source_text}&rdquo;
                      </p>
                    )}
                    <p className="text-xs text-neutral-500 leading-snug">
                      {ref.relevance}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
};

export default PlayEvidenceList;
